/**
 * Validation utilities for PostHog events
 */

import { PosthogEventProperties } from '~/services/posthog';

/**
 * Validate event name format
 */
export const isValidEventName = (eventName: string): boolean => {
  if (!eventName || typeof eventName !== 'string') {
    return false;
  }

  // Event name should follow pattern: entity:category:action
  const pattern = /^[a-z]+:[a-z]+:[a-z_]+$/;
  return pattern.test(eventName);
};

/**
 * Validate event properties
 */
export const validateEventProperties = (
  properties: PosthogEventProperties | undefined
): PosthogEventProperties | undefined => {
  if (!properties) return undefined;

  // Remove any undefined or null values
  const cleaned: PosthogEventProperties = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined && value !== null) {
      // Limit string lengths to prevent excessive data
      if (typeof value === 'string' && value.length > 1000) {
        cleaned[key] = value.substring(0, 1000) + '...';
      } else {
        cleaned[key] = value;
      }
    }
  }

  return cleaned;
};

/**
 * Validate user traits
 */
export const validateUserTraits = (traits: Record<string, any>): Record<string, any> => {
  const validTraits: Record<string, any> = {};

  // Whitelist of allowed user trait keys
  const allowedKeys = [
    'email',
    'role',
    'officeId',
    'officeName',
    'subscriptionTier',
    'createdAt',
    'lastLogin',
    'loginCount',
    'isParentAccount',
    'childOfficeCount',
    'teamSize',
    'accountType',
    'isVerified',
    'hasCompletedOnboarding',
  ];

  for (const [key, value] of Object.entries(traits)) {
    if (allowedKeys.includes(key) && value !== undefined && value !== null) {
      validTraits[key] = value;
    }
  }

  return validTraits;
};

/**
 * Check if tracking should be enabled for current environment
 */
export const shouldTrackInEnvironment = (): boolean => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  const disableTracking = process.env.REACT_APP_POSTHOG_DISABLE_TRACKING === 'true';

  // Don't track in test environment
  if (env === 'test') {
    return false;
  }

  // Check if explicitly disabled
  if (disableTracking) {
    return false;
  }

  // Track in production and staging by default
  if (env === 'production' || env === 'staging') {
    return true;
  }

  // In development, only track if debug mode is enabled
  if (env === 'development') {
    return process.env.REACT_APP_POSTHOG_DEBUG === 'true';
  }

  return false;
};

/**
 * Validate and limit array sizes in properties
 */
export const limitArraySizes = (
  properties: PosthogEventProperties,
  maxArraySize: number = 50
): PosthogEventProperties => {
  const limited: PosthogEventProperties = {};

  for (const [key, value] of Object.entries(properties)) {
    if (Array.isArray(value)) {
      // Limit array size and add indicator if truncated
      if (value.length > maxArraySize) {
        limited[key] = value.slice(0, maxArraySize);
        limited[`${key}_truncated`] = true;
        limited[`${key}_original_length`] = value.length;
      } else {
        limited[key] = value;
      }
    } else if (typeof value === 'object' && value !== null) {
      // Recursively handle nested objects
      limited[key] = limitArraySizes(value as PosthogEventProperties, maxArraySize);
    } else {
      limited[key] = value;
    }
  }

  return limited;
};